import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';

import { DM_ROUTE_PART, HYBRID_MODULES, RoutesDictionaryByCode, URL_ROUTE_PART } from '@legacy/constants';
import { User } from '@models/core';
import { MenuModel, MenuModuleTypeEnum, MenuType, RouteCodeEnum } from '@models/menu';
import { Role } from '@models/module/main';
import { Permission } from '@models/permission';
import { SYSTEM_FOLDERS } from '@module-instance/inbox/common/models/inbox-list.models';
import { HttpService } from '@shared/services/http.service';
import { TenantStore } from '@signal-store/tenant.store';
import { ContextInfo } from '@system/context';

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  private http = inject(HttpService);
  private tenantStore = inject(TenantStore);
  private tenantName = '';

  getModuleUserRole(id: number, contractId?: number) {
    const params = this.http.buildHttpParams({
      id,
      permissionId: Permission.Create,
      ...(contractId ? { contractId } : {}),
    });
    return this.http.get<Role[]>('_api/ModuleDefinition/ModuleRoleUserDict', { httpOptions: { params } });
  }

  getUserData() {
    return this.http.get<User>('_api/Main/UserData', { useLoader: false });
  }

  getUnreadNotifications(contractId: number) {
    const params = this.http.buildHttpParams({ contractId });
    return this.http.get<number>('_api/Folder/TotalUnreadCount', { httpOptions: { params }, useLoader: false });
  }

  getContextByEntityInstanceId(entityInstanceId: number) {
    const params = this.http.buildHttpParams({ entityInstanceId });
    return this.http.get<ContextInfo>('_api/Contracts/GetContextByEntityInstanceId', {
      httpOptions: { params },
      useLoader: false,
    });
  }

  getUserMenu(contractId?: number, entityInstanceId?: number): Observable<MenuModel[]> {
    this.tenantName = this.tenantStore.selectedTenant()?.name?.toLowerCase();
    if (!this.tenantName) {
      return of([]);
    }
    const params = this.http.buildHttpParams({ contractId });
    return this.http.get<MenuModel[]>('_api/Menu/UserMenu', { useLoader: false, httpOptions: { params } }).pipe(
      map(items => {
        this.setUserMenuUrlPath(items, contractId, entityInstanceId);
        return items;
      }),
    );
  }

  setLastVisitedDashboardModule(moduleId: number) {
    const params = this.http.buildHttpParams({ moduleId });
    return this.http.post(
      '_api/User/SetLastVisitedDashboardModule',
      {},
      {
        httpOptions: { params },
        useLoader: false,
      },
    );
  }

  setLastVisitedModule(moduleId: number) {
    const params = this.http.buildHttpParams({ moduleId });
    return this.http.post(
      '_api/User/SetLastVisitedModule',
      {},
      {
        httpOptions: { params },
        useLoader: false,
      },
    );
  }

  setUserMenuUrlPath(menuItems: MenuModel[], contractId?: number, entityInstanceId?: number) {
    menuItems.forEach(menuItem => this.applyUrlPath(menuItem, contractId, entityInstanceId));
  }

  isMenuItemMatchingRoute(menuItem: MenuModel): boolean {
    // The route from window.location.pathname has this format: /tenant_name/module_path...
    //   We remove the first '/' to have the same format as the menuItem.urlPath
    const urlRouterParts = window.location.pathname.slice(1).split('/');
    if (!urlRouterParts[1]) {
      return false;
    }
    const urlMenuParts = menuItem.urlPath?.split('/') ?? [];
    if (!urlMenuParts[1]) {
      return false;
    }
    // The menuItem.urlPath has this format: tenant_name/module_path....
    if (
      urlMenuParts[1] === DM_ROUTE_PART ||
      urlMenuParts[1] === RoutesDictionaryByCode[RouteCodeEnum.ManagementReports]
    ) {
      // Check only the moduleId part of the url for module-specific routes.
      return urlMenuParts[3] === urlRouterParts[3];
    }

    if (urlMenuParts[1] === URL_ROUTE_PART) {
      // The route is url/:moduleId — several menu items can share this route type, so the
      // moduleId part must also match, otherwise the first url-type menu item always wins.
      return urlMenuParts[1] === urlRouterParts[1].split('?')[0] && urlMenuParts[2] === urlRouterParts[2];
    }

    return urlRouterParts[1].split('?')[0] === urlMenuParts[1].split('?')[0];
  }

  private isMenuItemRoutePartDynamic(menuItem: MenuModel) {
    return !(menuItem.moduleId < 0 && !HYBRID_MODULES.includes(menuItem.moduleId));
  }

  private applyUrlPath(menuItem: MenuModel, contractId?: number, entityInstanceId?: number) {
    if (menuItem.children) {
      menuItem.children.forEach(item => this.applyUrlPath(item, contractId, entityInstanceId));
    } else if (menuItem.typeId != MenuType.Folder) {
      const { moduleId, code, perContract, moduleType } = menuItem;
      let routeData = '';
      let routePart = '';

      if (!this.isMenuItemRoutePartDynamic(menuItem)) {
        switch (code) {
          case RouteCodeEnum.ProgressPayment:
            routeData = `/${contractId ?? 0}`;
            break;
          case RouteCodeEnum.SOV:
            routeData = `/${contractId ?? 0}`;
            break;
          case RouteCodeEnum.Correspondence:
            routeData = `/${contractId ?? 0}/${SYSTEM_FOLDERS.INBOX}`;
            break;
          case RouteCodeEnum.ManagementReports:
            routeData = `/${contractId ?? 0}/${moduleId}`;
            break;
          case RouteCodeEnum.FileContractManager:
            routeData = `/${entityInstanceId ?? 0}`;
            break;
          default:
            break;
        }
        routePart = RoutesDictionaryByCode[code];
      } else {
        if (moduleType === MenuModuleTypeEnum.Reporting) {
          routePart = RoutesDictionaryByCode.MREP;
          routeData = `/0/${moduleId}`;
        } else {
          const ctrId = perContract ? (contractId ?? 0) : 0;
          routePart = menuItem.url ? URL_ROUTE_PART : DM_ROUTE_PART;
          routeData = menuItem.url ? `/${moduleId}` : `/${ctrId}/${moduleId}`;
        }
      }

      menuItem.urlPath = `${this.tenantName}/${routePart ?? ''}${routeData ?? ''}`;
    }
  }
}
